import React, { useState } from 'react';
import { MessageSquare, Zap, Bell, FileText, Home, Search, BarChart, Clock, ThumbsUp, Users, ChevronLeft, ChevronRight } from 'lucide-react';

const features = [
  {
    icon: <MessageSquare size={28} />,
    title: '口コミ自動収集',
    description: 'Google Business Profileと連携し、すべての口コミを自動で収集・管理'
  },
  {
    icon: <Zap size={28} />,
    title: 'AI返信文自動生成',
    description: 'オーナーの口調を学習し、自然な返信文をAIが自動生成'
  },
  {
    icon: <Bell size={28} />,
    title: 'Google連携でワンクリック同期',
    description: 'Googleアカウントを連携するだけで、ワンクリックで最新の口コミを取得'
  },
  {
    icon: <FileText size={28} />,
    title: '定型文・キーワード設定',
    description: '頻出する質問や内容に対して、最適な返信テンプレートを用意'
  },
  {
    icon: <Home size={28} />,
    title: '多店舗管理対応',
    description: '複数店舗の口コミを一元管理。店舗ごとの分析も可能'
  },
  {
    icon: <BarChart size={28} />,
    title: '口コミ統計・分析',
    description: '評価の分布や傾向を可視化し、時系列での変化を分析'
  }
];

const demoImages = [
  { src: "/img/demo1.png", alt: "デモ画像1" },
  { src: "/img/demo2.png", alt: "デモ画像2" },
  { src: "/img/demo3.png", alt: "デモ画像3" },
  { src: "/img/demo4.png", alt: "デモ画像4" },
  { src: "/img/demo5.png", alt: "デモ画像5" },
  { src: "/img/demo6.png", alt: "デモ画像6" },
];

const Features: React.FC = () => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const nextImage = () => {
    setCurrentImageIndex((prevIndex) => 
      prevIndex === demoImages.length - 1 ? 0 : prevIndex + 1
    );
  };
  const prevImage = () => {
    setCurrentImageIndex((prevIndex) => 
      prevIndex === 0 ? demoImages.length - 1 : prevIndex - 1
    );
  };
  return (
    <section id="features" className="section bg-gray-50">
      <div className="container">
        <div className="text-center mb-16">
          <h2 className="mb-4 text-3xl md:text-4xl font-semibold">シンプルで強力な機能</h2>
          <p className="max-w-2xl mx-auto text-lg">
            最小限の操作で最大限の効果を発揮する、洗練された機能群
          </p>
        </div>
        <div className="relative mb-16">
          <div className="bg-gray-800 rounded-2xl overflow-hidden shadow-apple-lg p-4 md:p-6 max-w-5xl mx-auto">
            <div className="relative pb-[56.25%]">
              <div className="absolute inset-0 bg-white rounded-lg overflow-hidden">
                <img 
                  src={demoImages[currentImageIndex].src}
                  alt={demoImages[currentImageIndex].alt}
                  className="w-full h-full object-contain bg-white"
                />
                <button
                  onClick={prevImage}
                  className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white p-2 rounded-full shadow-lg transition-all duration-300"
                  aria-label="Previous image"
                >
                  <ChevronLeft size={24} className="text-gray-700" />
                </button>
                <button
                  onClick={nextImage}
                  className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white p-2 rounded-full shadow-lg transition-all duration-300"
                  aria-label="Next image"
                >
                  <ChevronRight size={24} className="text-gray-700" />
                </button>
                <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex space-x-2">
                  {demoImages.map((_, index) => (
                    <button
                      key={index}
                      onClick={() => setCurrentImageIndex(index)}
                      className={`w-2 h-2 rounded-full transition-all duration-300 ${
                        index === currentImageIndex ? 'bg-blue-600 w-4' : 'bg-gray-300'
                      }`}
                      aria-label={`Go to image ${index + 1}`}
                    />
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {features.map((feature, index) => (
            <div 
              key={index}
              className="bg-white p-8 rounded-2xl shadow-apple transition-all duration-300 hover:shadow-apple-lg"
            >
              <div className="text-blue-600 mb-4">{feature.icon}</div>
              <h3 className="text-xl font-medium mb-2">{feature.title}</h3>
              <p>{feature.description}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Features;